<template lang="pug">
  div(:style="widthStyle")
    v-textarea.round-lg.yz-regular-body-sm(:class="colorClass" :style="colorStyle" v-model="modelValue" v-bind="filteredAttrs" v-on="$listeners" :rules="mergedRules" outlined, :hide-details="hideDetails")
</template>

<script>

export default {
  name: 'YzTextarea',
  props: {
    value: {
      default: null
    },
    backgroundColor: {
      type: String,
      default: 'white'
    },
    width: {
      type: String,
      default: 'auto'
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto"
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    },
    rules: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    modelValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
      }
    },
    filteredAttrs() {
      // eslint-disable-next-line no-unused-vars
      const { outlined, filled, dense, label, hideDetails, shaped, singleLine, solo, ...attrs } = this.$attrs
      return attrs
    },
    colorClass() {
      return 'yz-textarea-custom-bg'
    },
    colorStyle() {
      const style = {}
      
      // Set CSS custom property for background color
      if (this.backgroundColor) {
        let bgColor
        if (this.backgroundColor.startsWith('#')) {
          bgColor = this.backgroundColor
        } else if (this.backgroundColor.startsWith('var(')) {
          bgColor = this.backgroundColor
        } else if (this.backgroundColor.startsWith('yz-')) {
          bgColor = `var(--${this.backgroundColor})`
        } else {
          bgColor = `var(--yz-${this.backgroundColor})`
        }
        style['--yz-textarea-bg-color'] = bgColor
      }
      
      return style
    },
    widthStyle() {
      const style = {}
      
      if (this.width && this.width !== 'auto') {
        let widthValue
        
        // Handle predefined sizes
        if (this.width === 'full') {
          widthValue = '100%'
        } else if (this.width === 'fit-content') {
          widthValue = 'fit-content'
        } else {
          // Use the width value as-is for CSS units (px, %, rem, em, vw, vh, etc.)
          widthValue = this.width
        }
        
        style.width = widthValue
      }
      
      return style
    },
    mergedRules() {
      const rules = [...this.rules];
      
      // Skip required validation if field is disabled
      if (this.$attrs.disabled || this.$attrs.disabled === '') {
        return rules;
      }
      
      // If required is true, prepend the required validation rule
      if (this.required || this.required === '') {
        // Get input type from attrs, default to 'text' for textarea
        const inputType = this.$attrs.type || 'text';
        
        // Type-aware required validation
        const requiredRule = (v) => {
          switch (inputType) {
            case 'number':
              return (v != null && v !== '' && !isNaN(v)) || this.requiredErrorMessage;
            case 'email':
              return (v != null && v !== '' && String(v).trim() !== '') || this.requiredErrorMessage;
            case 'date':
            case 'datetime-local':
            case 'time':
              return (v != null && v !== '') || this.requiredErrorMessage;
            default:
              // For text and other types (textarea is typically text)
              return (v != null && v !== '' && String(v).trim() !== '') || this.requiredErrorMessage;
          }
        };
        
        rules.unshift(requiredRule);
      }
      
      return rules;
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-textarea--outlined {
    fieldset {
      border-color: var(--yz-neutral-400);
    }

    > .v-input__control > .v-input__slot {
      min-height: 36px !important;
    }
  }
  
  .v-textarea--filled > .v-input__control > .v-input__slot,
  .v-textarea--full-width > .v-input__control > .v-input__slot,
  .v-textarea--outlined > .v-input__control > .v-input__slot {
    min-height: 36px !important;
  }
  
  // Additional specificity for textarea elements
  .v-textarea textarea {
    min-height: 36px !important;
  }
  
  // Target the input wrapper more specifically
  .v-input__slot {
    min-height: 36px !important;
  }
  .error--text {
    color: var(--yz-danger) !important;
    fieldset {
      border-color: var(--yz-danger);
    }
  }
  // Icon positioning for 36px height
  .v-input__prepend-outer,
  .v-input__append-outer {
    margin-top: 9px !important;
    margin-bottom: 9px !important;
    height: 18px !important;
    
    .v-input__icon {
      height: 18px !important;
      width: 18px !important;
      font-size: 18px !important;
    }
    
    .v-icon {
      height: 18px !important;
      width: 18px !important;
      font-size: 18px !important;
    }
    
    .v-icon__component {
      height: 18px !important;
      width: 18px !important;
    }
  }

  .v-input__prepend-inner,
  .v-input__append-inner {
    margin-top: 9px !important;
    margin-bottom: 9px !important;
    height: 18px !important;
    
    .v-input__icon {
      height: 18px !important;
      width: 18px !important;
      font-size: 18px !important;
    }
    
    .v-icon {
      height: 18px !important;
      width: 18px !important;
      font-size: 18px !important;
    }
  }

  // Additional specificity for icon sizing
  .v-textarea--outlined .v-input__prepend-inner .v-icon,
  .v-textarea--outlined .v-input__append-inner .v-icon {
    height: 18px !important;
    width: 18px !important;
    font-size: 18px !important;
    
    .v-icon__component {
      height: 18px !important;
      width: 18px !important;
    }
  }
  
  // Target the icon component directly
  .v-input__prepend-inner .v-icon__component,
  .v-input__append-inner .v-icon__component,
  .v-input__prepend-outer .v-icon__component,
  .v-input__append-outer .v-icon__component {
    height: 18px !important;
    width: 18px !important;
  }
  
  // Additional specificity for outer icons
  .v-textarea--outlined .v-input__prepend-outer .v-icon,
  .v-textarea--outlined .v-input__append-outer .v-icon {
    height: 18px !important;
    width: 18px !important;
    font-size: 18px !important;
    
    .v-icon__component {
      height: 18px !important;
      width: 18px !important;
    }
  }

  // Ensure icons are centered vertically
  .v-textarea--outlined .v-input__prepend-inner,
  .v-textarea--outlined .v-input__append-inner,
  .v-textarea--outlined .v-input__prepend-outer,
  .v-textarea--outlined .v-input__append-outer {
    align-self: flex-start;
    margin-top: 12px !important;
  }

  // Disabled and readonly state cursor
  textarea[disabled],
  textarea[readonly] {
    cursor: not-allowed !important;
  }

  // Custom background color support
  &.yz-textarea-custom-bg {
    .theme--light.v-textarea textarea {
      background-color: var(--yz-textarea-bg-color, white) !important;
    }

    // Also target the input slot for outlined variant
    .v-input__slot {
      background-color: var(--yz-textarea-bg-color, white) !important;
    }
  }
}
</style>